<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import DollarIcon from '@primeicons/vue/dollar';
import GaugeIcon from '@primeicons/vue/gauge';
import GlobeIcon from '@primeicons/vue/globe';
import SitemapIcon from '@primeicons/vue/sitemap';
import { useEndpoint } from '@apparatix/composables';
import DetailDrawerHeader from '@apparatix/leads/DetailDrawerHeader.vue';
import InventoryOverviewTab from './InventoryOverviewTab.vue';
import InventoryRelationshipTab from './InventoryRelationshipTab.vue';
import InventoryMeasurementTab from './InventoryMeasurementTab.vue';
import InventoryPricingTab from './InventoryPricingTab.vue';
import type { InventoryDetailTabName, InventoryOverview } from './types';
import { SharedStyles } from '../SharedStyles.ts';

type Props = {
  id: number;
  activeTab?: InventoryDetailTabName;
};

const props = defineProps<Props>();

const route = useRoute();
const router = useRouter();

const TAB_NAMES: InventoryDetailTabName[] = ['overview', 'relationship', 'measurement', 'pricing'];

const normalizeTab = (tab?: string): InventoryDetailTabName =>
  TAB_NAMES.includes(tab as InventoryDetailTabName) ? (tab as InventoryDetailTabName) : 'measurement';

const activeTab = ref<InventoryDetailTabName>(normalizeTab(props.activeTab));
const queryInvNumber = ref<string>((route.query.invNumber as string) || '');
const overview = ref<InventoryOverview | null>(null);

// Track which tabs have been opened so each tab's data loads only on first
// activation (and never blocks the others). The header loads the overview once
// (below) and hands it to the overview tab, so that tab needs no fetch of its own.
const visitedTabs = ref<Set<InventoryDetailTabName>>(new Set([activeTab.value]));

const title = computed(() => overview.value?.invNumber || queryInvNumber.value || `Inventory #${props.id}`);
const subtitle = computed(() => overview.value?.productType ?? '');

const { makeRequest: loadOverview, setEndpointUrl: setOverviewEndpoint } = useEndpoint<{ data: InventoryOverview }>({
  url: '',
  type: 'GET',
  successField: 'data',
  onSuccess: response => {
    overview.value = response.data;
  },
  toast: { showOnSuccess: false },
});

const fetchHeader = () => {
  overview.value = null;
  setOverviewEndpoint(`/manage-measurements/inventory/${props.id}/overview`);
  loadOverview();
};

const onTabChange = (value: string) => {
  const tab = normalizeTab(value);
  activeTab.value = tab;
  visitedTabs.value.add(tab);

  const base = (route.meta?.basePath as string) ?? '/measurements';
  router.replace({
    path: `${base}/detail/inventory/${props.id}/${tab}`,
    query: queryInvNumber.value ? { invNumber: queryInvNumber.value } : undefined,
  });
};

watch(
  () => props.id,
  () => {
    queryInvNumber.value = (route.query.invNumber as string) || '';
    activeTab.value = normalizeTab(props.activeTab);
    visitedTabs.value = new Set([activeTab.value]);
    fetchHeader();
  },
  { immediate: true },
);
</script>
<template>
  <DetailDrawerHeader
    type="Inventory"
    :title="title"
    :subtitle="subtitle"
  />

  <Tabs class="grow" :value="activeTab" @update:value="onTabChange">
    <TabList>
      <Tab value="overview" :class="SharedStyles.flyouts.tabClasses">
        <GlobeIcon />
        Overview
      </Tab>
      <Tab value="relationship" :class="SharedStyles.flyouts.tabClasses">
        <SitemapIcon />
        Relationship
      </Tab>
      <Tab value="measurement" :class="SharedStyles.flyouts.tabClasses">
        <GaugeIcon />
        Measurement
      </Tab>
      <Tab value="pricing" :class="SharedStyles.flyouts.tabClasses">
        <DollarIcon />
        Pricing
      </Tab>
    </TabList>
    <TabPanels class="!bg-[var(--neutral-background)] grow">
      <div class="flex flex-col gap-6 p-2 lg:p-9">
        <TabPanel value="overview">
          <InventoryOverviewTab v-if="visitedTabs.has('overview')" :id="props.id" :overview="overview" />
        </TabPanel>
        <TabPanel value="relationship">
          <InventoryRelationshipTab v-if="visitedTabs.has('relationship')" :id="props.id" />
        </TabPanel>
        <TabPanel value="measurement">
          <InventoryMeasurementTab v-if="visitedTabs.has('measurement')" :id="props.id" />
        </TabPanel>
        <TabPanel value="pricing">
          <InventoryPricingTab v-if="visitedTabs.has('pricing')" :id="props.id" />
        </TabPanel>
      </div>
    </TabPanels>
  </Tabs>
</template>
